import React, { useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';

const ComparisonChart = ({ 
    lastWeekValue, 
    currentValue, 
    unit = '', 
    title = '비교 차트',
    lastWeekLabel = '지난 주 평균',
    currentLabel = '현재 평균'
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (chartRef.current) {
            const ctx = chartRef.current.getContext('2d');
            
            // 기존 차트 인스턴스가 있다면 제거
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // 차이값 계산
            const difference = currentValue - lastWeekValue;
            const differenceText = difference >= 0 ? `+${difference}${unit}` : `${difference}${unit}`;

            // 차트 생성
            chartInstance.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: [lastWeekLabel, currentLabel],
                    datasets: [{
                        label: '측정값',
                        data: [lastWeekValue, currentValue],
                        backgroundColor: ['#6c757d', '#007bff'],
                        borderColor: ['#495057', '#0056b3'],
                        borderWidth: 2,
                        borderRadius: 8,
                        barThickness: 60
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: title,
                            font: {
                                size: 16,
                                weight: 'bold'
                            },
                            color: '#333'
                        },
                        legend: {
                            display: false
                        },
                        tooltip: {
                            callbacks: {
                                label: function(context) {
                                    return `${context.label}: ${context.parsed.y}${unit}`;
                                }
                            }
                        }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            grid: {
                                color: 'rgba(0,0,0,0.1)'
                            },
                            ticks: {
                                color: '#666'
                            }
                        },
                        x: {
                            grid: {
                                display: false
                            },
                            ticks: {
                                color: '#666'
                            }
                        }
                    },
                    animation: {
                        duration: 1000,
                        easing: 'easeInOutQuart'
                    }
                },
                plugins: [{
                    id: 'comparisonOverlay',
                    afterDraw: (chart) => {
                        const { ctx, chartArea, scales } = chart;
                        if (!chartArea) return;

                        const { left, top, right, bottom, width, height } = chartArea;
                        const yScale = scales.y;
                        
                        // 두 막대의 위치 계산
                        const barWidth = 60;
                        const barSpacing = (width - barWidth * 2) / 3;
                        const leftBarX = left + barSpacing + barWidth / 2;
                        const rightBarX = left + barSpacing * 2 + barWidth * 1.5;
                        
                        // 막대의 높이 계산
                        const leftBarHeight = (lastWeekValue / yScale.max) * height;
                        const rightBarHeight = (currentValue / yScale.max) * height;
                        const leftBarY = bottom - leftBarHeight;
                        const rightBarY = bottom - rightBarHeight;
                        
                        // 차이값 표시 영역 그리기
                        const minY = Math.min(leftBarY, rightBarY);
                        const maxY = Math.max(leftBarY, rightBarY);
                        const overlayHeight = Math.abs(maxY - minY);
                        
                        // 차이값 배경 영역
                        ctx.save();
                        ctx.fillStyle = difference >= 0 ? 'rgba(255, 193, 7, 0.3)' : 'rgba(220, 53, 69, 0.3)';
                        ctx.strokeStyle = difference >= 0 ? '#ffc107' : '#dc3545';
                        ctx.lineWidth = 2;
                        ctx.setLineDash([5, 5]);
                        
                        // 오버랩 영역 그리기
                        ctx.beginPath();
                        ctx.moveTo(leftBarX - barWidth/2, minY);
                        ctx.lineTo(rightBarX + barWidth/2, minY);
                        ctx.lineTo(rightBarX + barWidth/2, maxY);
                        ctx.lineTo(leftBarX - barWidth/2, maxY);
                        ctx.closePath();
                        ctx.fill();
                        ctx.stroke();
                        
                        // 차이값 텍스트
                        ctx.fillStyle = difference >= 0 ? '#856404' : '#721c24';
                        ctx.font = 'bold 14px Arial';
                        ctx.textAlign = 'center';
                        ctx.setLineDash([]);
                        
                        const textX = (leftBarX + rightBarX) / 2;
                        const textY = minY - 10;
                        
                        // 텍스트 배경
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                        const textWidth = ctx.measureText(differenceText).width;
                        ctx.fillRect(textX - textWidth/2 - 5, textY - 15, textWidth + 10, 20);
                        
                        // 텍스트 그리기
                        ctx.fillStyle = difference >= 0 ? '#856404' : '#721c24';
                        ctx.fillText(differenceText, textX, textY);
                        
                        // 연결선 그리기
                        ctx.strokeStyle = '#6c757d';
                        ctx.lineWidth = 1;
                        ctx.setLineDash([3, 3]);
                        
                        // 왼쪽 막대에서 오버랩 영역으로
                        ctx.beginPath();
                        ctx.moveTo(leftBarX, leftBarY);
                        ctx.lineTo(leftBarX, minY);
                        ctx.stroke();
                        
                        // 오른쪽 막대에서 오버랩 영역으로
                        ctx.beginPath();
                        ctx.moveTo(rightBarX, rightBarY);
                        ctx.lineTo(rightBarX, maxY);
                        ctx.stroke();
                        
                        ctx.restore();
                    }
                }]
            });
        }

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [lastWeekValue, currentValue, unit, title, lastWeekLabel, currentLabel]);

    return (
        <div style={{ width: '100%', height: '300px', position: 'relative' }}>
            <canvas ref={chartRef}></canvas>
        </div>
    );
};

export default ComparisonChart; 